import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import grapeService, { Grape, PaginatedResponse } from '../../../services/grapeService';
import { useAuth } from '../../../context/AuthContext';

const GrapeList: React.FC = () => {
  const navigate = useNavigate();
  const { isSystemAdmin } = useAuth();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [grapes, setGrapes] = useState<Grape[]>([]);
  
  const [pagination, setPagination] = useState({
    page: 0, // MUI uses 0-indexed pages
    pageSize: 20,
    totalItems: 0,
    totalPages: 0
  });

  // Function to fetch grapes based on current state
  const fetchGrapes = async () => {
    setLoading(true);
    try {
      // Get all grapes
      const response = await grapeService.getAllGrapes(
        pagination.page + 1, // API uses 1-indexed pages
        pagination.pageSize
      );
      
      setGrapes(response.data);
      setPagination({
        ...pagination,
        totalItems: response.pagination.totalItems,
        totalPages: response.pagination.totalPages
      });
    } catch (error) {
      console.error('Failed to fetch grapes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load grapes when component mounts or when pagination changes
  useEffect(() => {
    fetchGrapes();
  }, [pagination.page, pagination.pageSize]);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPagination({ ...pagination, page: newPage });
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({
      ...pagination,
      pageSize: parseInt(event.target.value, 10),
      page: 0 // Reset to first page
    });
  };

  // Handle edit grape
  const handleEditGrape = (event: React.MouseEvent, grape: Grape) => {
    event.stopPropagation();
    navigate(`/admin/grapes/edit/${grape._id}`);
  };

  // Handle delete grape
  const handleDeleteGrape = async (event: React.MouseEvent, grape: Grape) => {
    event.stopPropagation();
    if (window.confirm(`Are you sure you want to delete the grape "${grape.primaryName}"?`)) {
      try {
        await grapeService.deleteGrape(grape._id);
        // Refresh the list
        fetchGrapes();
      } catch (error) {
        console.error('Failed to delete grape:', error);
        alert('Failed to delete grape. Please try again.');
      }
    }
  };

  // Get color display name
  const getColorDisplayName = (color: string): string => {
    switch (color) {
      case 'RED': return 'Red';
      case 'WHITE': return 'White';
      case 'PINK_ROSE': return 'Rosé';
      default: return color;
    }
  };

  // Get color chip based on grape color
  const getColorChip = (color: string) => {
    let chipColor = '';
    let textColor = '';
    
    switch (color) {
      case 'RED':
        chipColor = '#8B0000';
        textColor = 'white';
        break;
      case 'WHITE':
        chipColor = '#F0E68C';
        textColor = 'black';
        break;
      case 'PINK_ROSE':
        chipColor = '#FFB6C1';
        textColor = 'black';
        break;
      default:
        chipColor = 'default';
    }
    
    return (
      <Chip 
        label={getColorDisplayName(color)} 
        size="small" 
        sx={{ 
          backgroundColor: chipColor, 
          color: textColor,
          fontWeight: 'medium'
        }} 
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/grapes/create')}
          >
            Add Grape
          </Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : grapes.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ p: 3 }}>
            No grapes found. Click "Add Grape" to create one.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Primary Name</TableCell>
                    <TableCell>Color</TableCell>
                    <TableCell>Aliases</TableCell>
                    <TableCell>Species</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grapes.map((grape) => (
                    <TableRow 
                      key={grape._id}
                      hover
                      onClick={() => navigate(`/admin/grapes/edit/${grape._id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{grape.primaryName}</TableCell>
                      <TableCell>{getColorChip(grape.color)}</TableCell>
                      <TableCell>
                        {grape.aliases.length > 0 ? (
                          <Typography variant="body2">
                            {grape.aliases.slice(0, 2).map(alias => alias.name).join(', ')}
                            {grape.aliases.length > 2 && ` +${grape.aliases.length - 2} more`}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{grape.species || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small"
                            onClick={(e) => handleEditGrape(e, grape)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={(e) => handleDeleteGrape(e, grape)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={pagination.totalItems}
              rowsPerPage={pagination.pageSize}
              page={pagination.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default GrapeList;
